import { api } from '@organization/api'
import { LOGIN_URL } from '@organization/app-constants'
import { AuthWrapper } from '@organization/features/authentication/components/AuthWrapper'
import { showApiErrors } from '@shared/utils'
import { useMutation } from '@tanstack/react-query'
import { App, Button, Card, Flex, Form, Input, Typography } from 'antd'
import { ArrowLeft2 } from 'iconsax-react'
import { useEffect } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useStyle } from '../components/AuthWrapper/styles'

const redirectUrl = `${window.location.origin}/authentication/reset-password-confirm?token=`

export const ResetPasswordForm = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const { modal, notification } = App.useApp()
  const { styles } = useStyle()

  useEffect(() => {
    form.setFieldsValue({ redirect_url: redirectUrl })
  }, [form])

  const { mutate, isPending } = useMutation({
    mutationFn: api.auth.restore,

    onSuccess: () => {
      modal.success({
        title: t('modal.restore_password'),
        onOk: () => navigate(LOGIN_URL)
      })
    },

    onError: (e) => {
      showApiErrors(e, form)
    }
  })

  const onSubmit = async () => {
    if (!executeRecaptcha) {
      notification.error({
        message: t('error.recaptcha_unavailable'),
        description: t('error.recaptcha_unavailable_description')
      })
      return
    }

    const data = form.getFieldsValue()

    try {
      const recaptcha_token = await executeRecaptcha('restore_password')
      mutate({ ...data, recaptcha_token })
    } catch {
      notification.error({
        message: t('error.recaptcha_failed'),
        description: t('error.recaptcha_failed_description')
      })
    }
  }

  return (
    <AuthWrapper>
      <Card className={styles.card}>
        <Flex vertical gap={16}>
          <Button
            type="default"
            icon={<ArrowLeft2 size={16} />}
            onClick={() => navigate(LOGIN_URL)}
          />

          <Typography.Title level={4}>
            {t('auth.forgot_password_header')}?
          </Typography.Title>
          <Typography.Text>
            {t('auth.forgot_password_description')}
          </Typography.Text>
        </Flex>

        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={onSubmit}
          className={styles.form}
        >
          <Form.Item name="redirect_url" hidden>
            <Input autoComplete="off" />
          </Form.Item>

          <Form.Item
            label={t('form.item.email')}
            name="username"
            rules={[
              { type: 'email', message: t('form.message.invalid_email') },
              { required: true, message: t('form.message.email_required') }
            ]}
          >
            <Input placeholder={t('form.placeholder.email')} />
          </Form.Item>

          <Flex vertical gap={16} align="center" justify="center">
            <Button
              block
              type="primary"
              loading={isPending}
              disabled={!executeRecaptcha}
              htmlType="submit"
            >
              {t('button.submit')}
            </Button>
          </Flex>
        </Form>
      </Card>
    </AuthWrapper>
  )
}
